/**
 * Time — `docs/visit-page.md` §11, §6.5 (Board 7 A the tab, B *Edit a punch*)
 * and §27.4 (Board 18 E *Add time for the crew*, the same popup in both modes).
 *
 * Every punch, per day and person (do-not-substitute 9): the bar from 6 AM to
 * 6 PM — green on this visit, grey for the yard and the drive, stripes for
 * lunch, the plan dashed behind today's live bar — the punches in words, *on
 * this visit* in hours (one decimal), *on the clock* in hours and minutes, and
 * the cost (internal). Any change needs a reason and keeps who, when and why
 * (`punch_events` corrections, §4.9).
 */
import { z } from 'zod';
/** The punches the page names (*Edit a punch*'s **Punch**: *Clock in · Arrived · Start lunch · End lunch · Left · Clock out*). */
export const PUNCH_KINDS = ['clock_in', 'arrived', 'lunch_start', 'lunch_end', 'left', 'clock_out'];
// ---------------------------------------------------------------------------
// Writes
// ---------------------------------------------------------------------------
const moment = z.string().datetime({ offset: true });
const reason = z.string().trim().min(1, 'Say why.').max(2000);
/** Where a punch was — *at Keystone Equipment Rental*, *at Harrisburg yard*. */
export const punchPlaceSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('yard'), yardId: z.string().uuid() }),
    z.object({ kind: z.literal('vendor'), vendorId: z.string().uuid() }),
    z.object({ kind: z.literal('site') }),
]);
const addTimeFields = {
    /** **Person**. */
    userId: z.string().uuid(),
    /** **Day** — *Day 1 · Wed Oct 21*. */
    day: z.number().int().min(1).max(366),
    /** **Clocked in** *at …*. */
    clockInAt: moment,
    clockInPlace: punchPlaceSchema,
    /** **Arrived**. */
    arrivedAt: moment,
    /** **Left**. */
    leftAt: moment,
    /** **Clocked out** *at …*. */
    clockOutAt: moment,
    clockOutPlace: punchPlaceSchema,
    /** **Lunch from** · **Lunch to**. */
    lunchFrom: moment.nullish(),
    lunchTo: moment.nullish(),
};
const inOrder = (body) => {
    const at = (value) => Date.parse(value);
    if (!(at(body.clockInAt) <= at(body.arrivedAt) && at(body.arrivedAt) <= at(body.leftAt)
        && at(body.leftAt) <= at(body.clockOutAt)))
        return false;
    if ((body.lunchFrom == null) !== (body.lunchTo == null))
        return false;
    if (body.lunchFrom && body.lunchTo) {
        return at(body.arrivedAt) <= at(body.lunchFrom) && at(body.lunchFrom) <= at(body.lunchTo)
            && at(body.lunchTo) <= at(body.leftAt);
    }
    return true;
};
/**
 * `POST /visits/:id/time` — **Add time** / **Add the time** (Board 7, Board
 * 18 E): `{userId, day, clockInAt, clockInPlace, arrivedAt, leftAt,
 * clockOutAt, clockOutPlace, lunchFrom, lunchTo, reason}` (§27.4 replaces
 * §5.2's body). Through `CrewPort.addTime` → `punch_events` corrections
 * (`source = 'office'`, `entered_by`, `reason`) that create the intervals.
 */
export const addTimeSchema = z.object({
    ...addTimeFields,
    /** **Why** (required) — *Add the time* is disabled until it has text. */
    reason,
}).refine(inOrder, { message: 'The times are out of order.' });
/**
 * `POST /visits/:id/time/preview` — the popup's line *9.0 h on this visit ·
 * 10:15 on the clock* and its internal cost (*$265.50*), for what is typed.
 */
export const addTimePreviewSchema = z.object(addTimeFields).refine(inOrder, { message: 'The times are out of order.' });
/**
 * `PATCH /punches/:intervalId` — **Edit a punch** (Board 7 B): `{startedAt?,
 * endedAt?, reason}` → a correction (§4.9). **Save** is disabled until there
 * is a reason; *The original stays in History.*
 */
export const editPunchSchema = z.object({
    startedAt: moment.optional(),
    endedAt: moment.optional(),
    reason,
    /** The visit page it was pressed on, so the answer is its Time tab. */
    visitId: z.string().uuid(),
}).refine((body) => body.startedAt !== undefined || body.endedAt !== undefined, { message: 'Change a time.' });
/** `POST /visits/:id/time/flags/:flagId/looked-at` — **Look at it** on a flag (§11). */
export const lookAtFlagSchema = z.object({
    note: z.string().trim().max(2000).default(''),
});
//# sourceMappingURL=time.js.map